/**
 * HR & People routes, inside the platform shell under /hr.
 *
 * The HR screens as they were built, now reading the platform session. HR's
 * own login, role matrix and placeholder page are not here: sign-in is the
 * platform's, and roles are edited under Administration -> Roles & permissions.
 */
import { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';
import { Spinner, PermissionDenied } from '@/components/shared/states';
import { usePermissions } from '@/stores/session';

const page = <T extends Record<string, React.ComponentType>>(loader: () => Promise<T>, name: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[name] as React.ComponentType })));

const DashboardPage = page(() => import('./pages/DashboardPage'), 'DashboardPage');
const MySpacePage = page(() => import('./pages/MySpacePage'), 'MySpacePage');
const EmployeesPage = page(() => import('./pages/EmployeesPage'), 'EmployeesPage');
const EmployeeDetailPage = page(() => import('./pages/EmployeeDetailPage'), 'EmployeeDetailPage');
const RecruitmentPage = page(() => import('./pages/RecruitmentPage'), 'RecruitmentPage');
const LettersPage = page(() => import('./pages/LettersPage'), 'LettersPage');
const AttendancePage = page(() => import('./pages/AttendancePage'), 'AttendancePage');
const PunchPage = page(() => import('./pages/PunchPage'), 'PunchPage');
const AttendanceReportPage = page(() => import('./pages/AttendanceReportPage'), 'AttendanceReportPage');
const RegularisationsPage = page(() => import('./pages/RegularisationsPage'), 'RegularisationsPage');
const AttendanceRulesPage = page(() => import('./pages/AttendanceRulesPage'), 'AttendanceRulesPage');
const LabourAttendancePage = page(() => import('./pages/LabourAttendancePage'), 'LabourAttendancePage');
const BiometricDevicesPage = page(() => import('./pages/BiometricDevicesPage'), 'BiometricDevicesPage');
const ApprovalWorkflowPage = page(() => import('./pages/ApprovalWorkflowPage'), 'ApprovalWorkflowPage');
const LeavePage = page(() => import('./pages/LeavePage'), 'LeavePage');
const PayrollPage = page(() => import('./pages/PayrollPage'), 'PayrollPage');
const ExpensesPage = page(() => import('./pages/ExpensesPage'), 'ExpensesPage');
const LoansPage = page(() => import('./pages/LoansPage'), 'LoansPage');
const PerformancePage = page(() => import('./pages/PerformancePage'), 'PerformancePage');
const TrainingPage = page(() => import('./pages/TrainingPage'), 'TrainingPage');
const ExitPage = page(() => import('./pages/ExitPage'), 'ExitPage');
const ReportsPage = page(() => import('./pages/ReportsPage'), 'ReportsPage');
const OrgStructurePage = page(() => import('./pages/OrgStructurePage'), 'OrgStructurePage');
const HolidayCalendarPage = page(() => import('./pages/HolidayCalendarPage'), 'HolidayCalendarPage');
const MasterRoutePage = page(() => import('./pages/MasterRoutePage'), 'MasterRoutePage');
const SettingsPage = page(() => import('./pages/SettingsPage'), 'SettingsPage');
const UsersPage = page(() => import('./pages/UsersPage'), 'UsersPage');
const AuditPage = page(() => import('./pages/AuditPage'), 'AuditPage');

/** Same module keys as the sidebar; the server enforces the finer checks. */
function Gate({ module, children }: { module: string; children: React.ReactNode }) {
  const { canAccess } = usePermissions();
  if (!canAccess(module)) return <PermissionDenied />;
  return <Suspense fallback={<Spinner label="Loading…" />}>{children}</Suspense>;
}

const wrap = (element: React.ReactNode, module: string) => <Gate module={module}>{element}</Gate>;

export function hrRoutes() {
  return [
    <Route key="hr" path="hr" element={wrap(<DashboardPage />, 'hr.selfservice')} />,
    <Route key="hr-me" path="hr/me" element={wrap(<MySpacePage />, 'hr.selfservice')} />,
    <Route key="hr-emps" path="hr/employees" element={wrap(<EmployeesPage />, 'hr.employee')} />,
    <Route key="hr-emp" path="hr/employees/:id" element={wrap(<EmployeeDetailPage />, 'hr.employee')} />,
    <Route key="hr-rec" path="hr/recruitment" element={wrap(<RecruitmentPage />, 'hr.recruitment')} />,
    <Route key="hr-let" path="hr/letters" element={wrap(<LettersPage />, 'hr.letter')} />,
    <Route key="hr-att" path="hr/attendance" element={wrap(<AttendancePage />, 'hr.attendance')} />,
    <Route key="hr-punch" path="hr/attendance/punch" element={wrap(<PunchPage />, 'hr.selfservice')} />,
    <Route key="hr-attrep" path="hr/attendance/report" element={wrap(<AttendanceReportPage />, 'hr.attendance')} />,
    <Route key="hr-reg" path="hr/attendance/regularisations" element={wrap(<RegularisationsPage />, 'hr.attendance')} />,
    <Route key="hr-rules" path="hr/attendance/rules" element={wrap(<AttendanceRulesPage />, 'master.employee')} />,
    <Route key="hr-labour" path="hr/attendance/labour" element={wrap(<LabourAttendancePage />, 'hr.attendance')} />,
    <Route key="hr-dev" path="hr/attendance/devices" element={wrap(<BiometricDevicesPage />, 'hr.attendance')} />,
    <Route key="hr-leave" path="hr/leave" element={wrap(<LeavePage />, 'hr.leave')} />,
    <Route key="hr-pay" path="hr/payroll" element={wrap(<PayrollPage />, 'hr.payroll')} />,
    <Route key="hr-exp" path="hr/expenses" element={wrap(<ExpensesPage />, 'hr.expense')} />,
    <Route key="hr-loans" path="hr/loans" element={wrap(<LoansPage />, 'hr.loan')} />,
    <Route key="hr-perf" path="hr/performance" element={wrap(<PerformancePage />, 'hr.performance')} />,
    <Route key="hr-train" path="hr/training" element={wrap(<TrainingPage />, 'hr.training')} />,
    <Route key="hr-exit" path="hr/exit" element={wrap(<ExitPage />, 'hr.exit')} />,
    <Route key="hr-rep" path="hr/reports" element={wrap(<ReportsPage />, 'hr.report')} />,
    <Route key="hr-org" path="hr/masters/hierarchy" element={wrap(<OrgStructurePage />, 'master.employee')} />,
    <Route key="hr-hol" path="hr/masters/holiday-calendar" element={wrap(<HolidayCalendarPage />, 'master.employee')} />,
    <Route key="hr-master" path="hr/masters/:resource" element={wrap(<MasterRoutePage />, 'master.employee')} />,
    <Route key="hr-set" path="hr/admin/settings" element={wrap(<SettingsPage />, 'admin.settings')} />,
    <Route key="hr-users" path="hr/admin/users" element={wrap(<UsersPage />, 'admin.user')} />,
    <Route key="hr-appr" path="hr/admin/approvals" element={wrap(<ApprovalWorkflowPage />, 'admin.settings')} />,
    <Route key="hr-audit" path="hr/admin/audit" element={wrap(<AuditPage />, 'admin.audit')} />,
  ];
}
